import type { DonationMode, EggIncubation, SurvivorFavorMode, WinCondition } from '../../types/game'
import type { HostDraft } from './lobbyDraft'

interface AdvancedSettingsModalProps {
  draft: HostDraft
  onChange: (draft: HostDraft) => void
  onBack: () => void
}

export function AdvancedSettingsModal({ draft, onChange, onBack }: AdvancedSettingsModalProps) {
  const setDonationMode = (donationMode: DonationMode) => onChange({ ...draft, donationMode })
  const setEggIncubation = (eggIncubation: EggIncubation) => onChange({ ...draft, eggIncubation })
  const setSurvivorFavorMode = (survivorFavorMode: SurvivorFavorMode) => onChange({ ...draft, survivorFavorMode })
  const setWinCondition = (winCondition: WinCondition) => onChange({ ...draft, winCondition })

  return (
    <>
      <div className="lobby-overlay" />
      <div className="lobby tableau">
        <div className="lobby-header">
          <div className="lobby-title">Advanced Settings</div>
        </div>

        <div className="host-room-options">
          <label className="lobby-game-option-row">
            <input
              type="checkbox"
              checked={draft.publicOffers}
              onChange={(event) => onChange({ ...draft, publicOffers: event.target.checked })}
            />
            <span>Resource offers are public</span>
          </label>

          <label className="lobby-game-option-row">
            <input
              type="checkbox"
              checked={draft.unlimitedQueenTrade}
              onChange={(event) => onChange({ ...draft, unlimitedQueenTrade: event.target.checked })}
            />
            <span>Unlimited Queen Trade</span>
          </label>

          <label className="lobby-game-option-row">
            <input
              type="checkbox"
              checked={draft.incrementalBroodChamberCost}
              onChange={(event) => onChange({ ...draft, incrementalBroodChamberCost: event.target.checked })}
            />
            <span>Incremental Brood Chamber Cost (ring 1: 1 wax, ring 2: 2 wax, ring 3: 3 wax)</span>
          </label>

          <label className="lobby-game-option-row">
            <input
              type="checkbox"
              checked={draft.infectionSpread}
              onChange={(event) => onChange({ ...draft, infectionSpread: event.target.checked })}
            />
            <span>Infections spread (a fully infected cell festers 1 infection toward the Queen each winter)</span>
          </label>

          <div className="lobby-game-options">
            <div className="lobby-donation-mode-label">Donation Mode</div>
            <div className="lobby-donation-mode">
              <label>
                <input
                  type="radio"
                  name="host-donation-mode"
                  checked={draft.donationMode === 'disabled'}
                  onChange={() => setDonationMode('disabled')}
                />
                <span>Players can not donate resources to one another</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="host-donation-mode"
                  checked={draft.donationMode === 'refusable'}
                  onChange={() => setDonationMode('refusable')}
                />
                <span>Donations are possible, but receiving player can refuse</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="host-donation-mode"
                  checked={draft.donationMode === 'non_refusable'}
                  onChange={() => setDonationMode('non_refusable')}
                />
                <span>Receiving players can not decline a donation</span>
              </label>
            </div>
          </div>

          <div className="lobby-game-options">
            <div className="lobby-donation-mode-label">Egg Incubation</div>
            <div className="lobby-donation-mode">
              <label>
                <input
                  type="radio"
                  name="host-egg-incubation"
                  checked={draft.eggIncubation === 'season'}
                  onChange={() => setEggIncubation('season')}
                />
                <span>Eggs hatch at the end of every season</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="host-egg-incubation"
                  checked={draft.eggIncubation === 'year'}
                  onChange={() => setEggIncubation('year')}
                />
                <span>Eggs only hatch after winter</span>
              </label>
            </div>
          </div>

          <div className="lobby-game-options">
            <div className="lobby-donation-mode-label">Survivor Favor</div>
            <div className="lobby-donation-mode">
              <label>
                <input
                  type="radio"
                  name="host-survivor-favor-mode"
                  checked={draft.survivorFavorMode === 'graded'}
                  onChange={() => setSurvivorFavorMode('graded')}
                />
                <span>Largest offering earns 5 Favor, second-largest 3</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="host-survivor-favor-mode"
                  checked={draft.survivorFavorMode === 'per_resource'}
                  onChange={() => setSurvivorFavorMode('per_resource')}
                />
                <span>1 Favor per offered resource that counted toward the requirements</span>
              </label>
            </div>
          </div>

          <div className="lobby-game-options">
            <div className="lobby-donation-mode-label">Win Condition</div>
            <div className="lobby-donation-mode">
              <label>
                <input
                  type="radio"
                  name="host-win-condition"
                  checked={draft.winCondition === 'built_and_clean'}
                  onChange={() => setWinCondition('built_and_clean')}
                />
                <span>At Winter's end: every cell is a Brood Chamber and no infection remains</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="host-win-condition"
                  checked={draft.winCondition === 'full_honey'}
                  onChange={() => setWinCondition('full_honey')}
                />
                <span>Every cell holds Honey (the game ends at once)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="lobby-start-container">
          <div id="advanced-settings-back-button" className="lobby-start" onClick={onBack}>
            Back
          </div>
        </div>
      </div>
    </>
  )
}
